import React, { useState } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { X, ClipboardCopy, Loader2, CheckCircle2 } from 'lucide-react';

// Admin-only workflow for review and MANUAL quote delivery.
// It never creates a payment, asserts customer acceptance, or schedules work.
// Firebase security rules must independently enforce administrator permissions.
export default function WorkOrderReviewModal({ job, adminId, onClose }) {
  const [scope, setScope] = useState(job.quote?.scope || '');
  const [exclusions, setExclusions] = useState(job.quote?.exclusions || '');
  const [price, setPrice] = useState(
    Number.isInteger(job.quote?.totalCents) ? (job.quote.totalCents / 100).toFixed(2) : ''
  );
  const [terms, setTerms] = useState(job.quote?.terms ||
    '50% deposit due after written acceptance of the quoted scope. Remaining balance due on completion, subject to agreed payment terms. Changes to scope require written authorization.');
  const [sentConfirmation, setSentConfirmation] = useState(false);
  const [revising, setRevising] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const currentQuote = job.quote || null;
  const existingVersion = Number.isInteger(currentQuote?.version) ? currentQuote.version : 0;
  const alreadySent = currentQuote?.status === 'sent' && job.status === 'quote_sent';
  const editable = !alreadySent || revising;
  const workingVersion = !currentQuote ? 1 : alreadySent && revising ? existingVersion + 1 : existingVersion;
  const amount = Number(price);
  const validAmount = Number.isFinite(amount) && amount > 0 && amount <= 1000000 && /^\d{1,7}(\.\d{1,2})?$/.test(price.trim());
  const totalCents = validAmount ? Math.round(amount * 100) : 0;
  const depositCents = Math.round(totalCents / 2);
  const currency = dollars => (dollars / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

  const quoteText = () => [
    `Chuko Logic — Service Quote v${workingVersion}`,
    `Request: ${job.id}`,
    `Client: ${job.clientName || job.clientEmail || 'Client'}`,
    `Service: ${job.serviceType || 'IT Service'}`,
    `Location: ${job.location || 'To be confirmed'}`,
    `Scope of work: ${scope.trim()}`,
    `Exclusions: ${exclusions.trim() || 'None specified; only the scope above is included.'}`,
    `Quoted total: ${currency(totalCents)}`,
    `Deposit (50%, after acceptance): ${currency(depositCents)}`,
    `Remaining balance: ${currency(totalCents - depositCents)}`,
    `Terms: ${terms.trim()}`,
    'Please reply in writing that you accept this scope, price, and terms, or request revisions. No appointment is confirmed and no payment is due until acceptance.',
    'dispatch@chukologic.com | (920) 724-1438'
  ].join('\n\n');

  const validateQuote = () => {
    if (scope.trim().length < 20) return 'Describe the authorized work with at least 20 characters.';
    if (!validAmount) return 'Enter a valid fixed quote between $0.01 and $1,000,000 (up to two decimal places).';
    if (terms.trim().length < 20) return 'Include meaningful payment and scope terms.';
    return '';
  };

  const saveReview = async () => {
    setError(''); setSuccess(''); setSaving(true);
    try {
      if (job.status !== 'pending' && job.status !== 'under_review') throw new Error('Only pending requests can be moved into review.');
      await updateDoc(doc(db, 'workOrders', job.id), {
        status: 'under_review',
        reviewStartedAt: new Date().toISOString(),
        reviewedBy: adminId || '',
        updatedAt: new Date().toISOString(),
      });
      setSuccess('Request moved to Under Review.');
    } catch (e) { setError(e.message || 'Unable to start review.'); }
    finally { setSaving(false); }
  };

  const saveQuote = async (markSent = false) => {
    setError(''); setSuccess('');
    if (!editable) { setError('This quote is already sent. Choose Prepare Revision to edit it.'); return; }
    const problem = validateQuote();
    if (problem) { setError(problem); return; }
    if (markSent && !sentConfirmation) {
      setError('Confirm that the complete quote has actually been sent to the customer before recording it as sent.');
      return;
    }
    if (['quote_accepted', 'pending_payment', 'assigned', 'in-progress', 'in_progress', 'completed', 'cancelled'].includes(job.status)) {
      setError('This job has passed quote review. Changes require a controlled amendment.');
      return;
    }
    setSaving(true);
    try {
      const now = new Date().toISOString();
      const quote = {
        version: workingVersion,
        scope: scope.trim(),
        exclusions: exclusions.trim(),
        terms: terms.trim(),
        totalCents,
        depositCents,
        currency: 'usd',
        status: markSent ? 'sent' : 'draft',
        preparedBy: adminId || '',
        preparedAt: now,
        ...(markSent ? { sentAt: now } : {}),
      };
      await updateDoc(doc(db, 'workOrders', job.id), {
        quote,
        status: markSent ? 'quote_sent' : 'quote_ready',
        updatedAt: now,
      });
      setSuccess(markSent ? 'Quote marked as sent. Await written client acceptance before taking the deposit.' : 'Quote draft saved. It has NOT been sent.');
      if (markSent) setSentConfirmation(false);
    } catch (e) { setError(e.message || 'Unable to save quote.'); }
    finally { setSaving(false); }
  };

  const copyQuote = async () => {
    if (!editable) { setError('Choose Prepare Revision to send a new version.'); return; }
    const problem = validateQuote();
    if (problem) { setError(problem); return; }
    try {
      await navigator.clipboard.writeText(quoteText());
      setSuccess('Quote text copied. Send it to the customer, then mark it as sent.');
      setError('');
    } catch (e) {
      setError('Clipboard unavailable. Copy the quote from the text preview below.');
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" role="presentation" onMouseDown={e => { if (e.target === e.currentTarget && !saving) onClose(); }}>
      <section role="dialog" aria-modal="true" aria-labelledby="review-quote-heading" className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[92vh] overflow-y-auto p-6 sm:p-8">
        <div className="flex justify-between items-start gap-4 mb-5">
          <div>
            <h2 id="review-quote-heading" className="text-2xl font-bold text-slate-900">Review Request / Prepare Quote</h2>
            <p className="text-sm text-slate-600 mt-1 break-all">{job.id}</p>
            <p className="text-sm text-slate-600 mt-1">Current status: <strong>{job.status || 'pending'}</strong></p>
          </div>
          <button aria-label="Close review" onClick={onClose} disabled={saving} className="p-2 rounded-lg hover:bg-slate-100"><X size={22}/></button>
        </div>

        <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 mb-5 text-sm space-y-1">
          <p><strong>Client:</strong> {job.clientName || job.clientEmail || job.clientId || 'Unknown'}</p>
          <p><strong>Email:</strong> {job.clientEmail || 'Not provided'}</p>
          <p><strong>Service:</strong> {job.serviceType || 'Not specified'}</p>
          <p><strong>Location:</strong> {job.location || 'Not provided'}</p>
          <p className="whitespace-pre-wrap"><strong>Request:</strong> {job.description || 'No description provided'}</p>
        </div>

        <div className="flex flex-wrap gap-2 mb-5">
          <button disabled={saving || !['pending', 'under_review'].includes(job.status || 'pending')} onClick={saveReview} className="px-4 py-2 border border-slate-300 rounded-lg text-sm font-semibold disabled:opacity-40">Mark Under Review</button>
          <span className="text-sm text-slate-500 self-center">A request is not an accepted quote or confirmed booking.</span>
        </div>

        {alreadySent && !revising && (
          <div className="p-4 mb-4 rounded-lg bg-blue-50 border border-blue-200 text-sm text-blue-900 flex items-center justify-between gap-3">
            <span>Quote v{existingVersion} is recorded as sent. Changes must create a new version.</span>
            <button className="border border-blue-400 px-3 py-2 rounded font-semibold shrink-0" onClick={() => { setRevising(true); setSentConfirmation(false); setSuccess(''); }}>Prepare Revision</button>
          </div>
        )}
        <fieldset disabled={!editable || saving} className="space-y-4 disabled:opacity-70">
          <div>
            <label htmlFor="review-scope" className="block text-sm font-semibold text-slate-800 mb-1">Quoted scope of work *</label>
            <textarea id="review-scope" rows={4} maxLength={4000} value={scope} onChange={e => setScope(e.target.value)} className="w-full border border-slate-300 rounded-lg p-3" placeholder="Specific deliverables and testing/acceptance criteria" />
          </div>
          <div>
            <label htmlFor="review-exclusions" className="block text-sm font-semibold text-slate-800 mb-1">Exclusions</label>
            <textarea id="review-exclusions" rows={2} maxLength={2000} value={exclusions} onChange={e => setExclusions(e.target.value)} className="w-full border border-slate-300 rounded-lg p-3" placeholder="Materials, additional cable runs, travel, etc." />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="review-total" className="block text-sm font-semibold text-slate-800 mb-1">Fixed quoted total ($) *</label>
              <input id="review-total" type="text" inputMode="decimal" value={price} onChange={e => setPrice(e.target.value)} placeholder="450.00" className="w-full border border-slate-300 rounded-lg p-3" />
            </div>
            <div className="bg-blue-50 border border-blue-100 rounded-lg p-3">
              <p className="text-xs text-slate-600">Deposit after acceptance (50%)</p>
              <p className="text-2xl font-bold text-blue-900">{currency(depositCents)}</p>
              <p className="text-xs text-slate-600">Balance: {currency(totalCents - depositCents)}</p>
            </div>
          </div>
          <div>
            <label htmlFor="review-terms" className="block text-sm font-semibold text-slate-800 mb-1">Terms *</label>
            <textarea id="review-terms" rows={3} maxLength={2000} value={terms} onChange={e => setTerms(e.target.value)} className="w-full border border-slate-300 rounded-lg p-3" />
          </div>
        </fieldset>

        <div className="mt-5 p-4 border border-slate-200 rounded-lg">
          <p className="font-semibold text-sm mb-2">Version {workingVersion} — Quote delivery (manual)</p>
          <button disabled={!editable || saving} onClick={copyQuote} type="button" className="inline-flex items-center gap-2 text-sm font-semibold border border-slate-300 rounded-lg px-3 py-2"><ClipboardCopy size={16} /> Copy quote text</button>
          <details className="mt-3"><summary className="cursor-pointer text-sm text-slate-600">Preview quote text</summary><pre className="mt-2 whitespace-pre-wrap break-words text-xs bg-slate-50 rounded p-3">{quoteText()}</pre></details>
        </div>

        <label className="mt-5 flex items-start gap-2 text-sm text-slate-800">
          <input type="checkbox" disabled={!editable || saving} className="mt-1" checked={sentConfirmation} onChange={e => setSentConfirmation(e.target.checked)} />
          <span>I have already sent this exact quote and version to the customer's verified contact address.</span>
        </label>
        {error && <p role="alert" className="mt-4 text-sm p-3 bg-red-50 text-red-800 border border-red-200 rounded">{error}</p>}
        {success && <p role="status" className="mt-4 text-sm p-3 bg-green-50 text-green-800 border border-green-200 rounded flex gap-2"><CheckCircle2 size={18} />{success}</p>}
        <div className="flex flex-wrap justify-end gap-3 mt-6">
          <button disabled={saving} onClick={onClose} className="px-4 py-2 border border-slate-300 rounded-lg font-semibold">Close</button>
          <button disabled={saving || !editable} onClick={() => saveQuote(false)} className="px-4 py-2 bg-slate-800 text-white rounded-lg font-semibold disabled:opacity-50">{saving ? 'Saving…' : 'Save Draft'}</button>
          <button disabled={saving || !editable || !sentConfirmation} onClick={() => saveQuote(true)} className="px-4 py-2 bg-blue-700 text-white rounded-lg font-semibold disabled:opacity-50">{saving ? <Loader2 className="animate-spin" size={18}/> : 'Record Quote Sent'}</button>
        </div>
      </section>
    </div>
  );
}
